import React, { useEffect, useState } from "react";
import { viewPDF } from "../../utils/viewPDF";
import { Box, Button, IconButton, Tooltip } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import axios from "axios";
import { CURRENCY_INR } from "../../utils/constant";

const ViewAllBidsTableNew = ({
  rfq,
  bidData,
  groupedData,
  groupedWorkItems,
  storedUser,
  applyValue,
  setApplyValue,
  getBidData
}) => {
  const [data, setData] = useState([]);
  const columnVisibility = {};

  let globalShowRate = false;
  let globalShowRemark = false;

  Object.values(groupedData).forEach((bids) => {
    bids.forEach((bid) => {
      const hasNonINR = bid.bidWorkItems.some(
        (item) => item.currency?.currency?.shortform !== CURRENCY_INR
      );

      const hasRemark = bid.bidWorkItems.some(
        (item) => item.remark && item.remark.trim() !== ""
      );

      if (hasNonINR) globalShowRate = true;
      if (hasRemark) globalShowRemark = true;
    });
  });

  // Second pass: apply global visibility to all
  Object.entries(groupedData).forEach(([vendor, bids]) => {
    bids.forEach((_, index) => {
      columnVisibility[`${vendor}-${index}`] = {
        showRate: globalShowRate,
        showRemark: globalShowRemark,
      };
    });
  });

  useEffect(() => {
    const leftRows = document.querySelectorAll("#leftTable tbody tr");
    const rightRows = document.querySelectorAll("#rightTable tbody tr");

    for (let i = 0; i < Math.min(leftRows.length, rightRows.length); i++) {
      const leftHeight = leftRows[i].getBoundingClientRect().height;
      const rightHeight = rightRows[i].getBoundingClientRect().height;

      const maxHeight = Math.max(leftHeight, rightHeight);

      leftRows[i].style.height = `${maxHeight}px`;
      rightRows[i].style.height = `${maxHeight}px`;
    }
  }, [groupedWorkItems, groupedData]);

  const latestBids =
    rfq && groupedData
      ? Object.entries(groupedData).map(([vendor, bids]) => {
          const mostRecent = [...bids].sort(
            (a, b) => new Date(b.createdDate) - new Date(a.createdDate)
          )[0];
          return { vendor, bid: mostRecent };
        })
      : [];

  const totals = latestBids.map(({ vendor, bid }) => {
  const baseTotal = bid.bidWorkItems.reduce((acc, item) => {
    const rate = item.rate || 0;
    const quantity = item.quantity || 0;
    const conversionRate = item.currency?.rate || 1;

    const subTotal = rate * quantity;
    return (
      acc +
      (item.currency.currency.shortform !== CURRENCY_INR
        ? subTotal * conversionRate
        : subTotal)
    );
  }, 0);

  const calculateExtra = (value, type) => {
    if (!value || !type) return 0;
    if (type === "percentage") return baseTotal * (value / 100);
    return value;
  };

  const packagingExtra = calculateExtra(bid.packagingValue, bid.packagingType);
  const freightExtra   = calculateExtra(bid.freightValue, bid.freightType);
  const creditExtra    = calculateExtra(bid.creditValue, bid.creditType);

  const finalTotal = baseTotal + packagingExtra + freightExtra + creditExtra;

  return { vendor, total: finalTotal };
});


  const sortedTotals = [...totals].sort((a, b) => a.total - b.total);
  let lowest = null;
  let secondLowest = null;

  if (sortedTotals.length > 0) {
    lowest = sortedTotals[0];
  }
  if (sortedTotals.length > 1) {
    secondLowest =
      sortedTotals.find((entry) => entry.vendor !== lowest.vendor) || null;
  }

  const handleChange = (bidId, field, value) => {
    setData((prev) => {
      const existing = prev.find((item) => item.bidId === bidId);

      if (existing) {
        return prev.map((item) =>
          item.bidId === bidId ? { ...item, [field]: value } : item
        );
      } else {
        return [...prev, { bidId, [field]: value }];
      }
    });
  };

  const enterValues = async () => {
    try {
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/bid/values`, data, {
        headers: { Authorization: `Bearer ${storedUser.accessToken}` },
      });
      setApplyValue(true);
      getBidData();
    } catch (error) {
      console.log(error);
    }
  };

  const getVendorColSpan = (vendor, bids) => {
    return bids.reduce((sum, _, index) => {
      const visibility = columnVisibility[`${vendor}-${index}`];
      let count = 2; // base: rate and currency
      if (visibility?.showRate) count += 1;
      if (visibility?.showRemark) count += 1;
      return sum + count;
    }, 0);
  };

  return (
    <div>
      <Box sx={{ display: "flex", overflow: "hidden" }}>
        {/* Fixed Left Table */}
        <table
          id="leftTable"
          style={{
            borderCollapse: "collapse",
            tableLayout: "fixed",
            textAlign: "center",
            border: "1px solid #ccc",
            marginBottom: "15px",
            width: "100px",
          }}
        >
          <thead>
            <tr>
              <th
                style={{
                  padding: "9px",
                  width: "100px",
                  height: "124px",
                }}
              >
                Item Code
              </th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(groupedWorkItems).map(
              ([section, items], sectionIndex) => (
                <React.Fragment key={sectionIndex}>
                  <tr
                    style={{
                      borderTop: "1px solid #ccc",
                      fontWeight: "bold",
                    }}
                  >
                    <td
                      colSpan={4}
                      style={{
                        textAlign: "left",
                        padding: "0px 10px",
                        height: "41px",
                      }}
                    >
                      Item Group: {section}
                    </td>
                  </tr>
                  {items
                    .sort((a, b) => a.itemOrder - b.itemOrder)
                    .map((item, i) => (
                      <tr key={i}>
                        <td
                          style={{
                            borderTop: "1px solid #ccc",
                            borderRight: "1px solid #ccc",
                          }}
                        >
                          {item.code}
                        </td>
                      </tr>
                    ))}
                </React.Fragment>
              )
            )}
            <tr>
              <td
                style={{
                  borderTop: "1px solid #ccc",
                  height: "40px",
                  fontWeight: "bold",
                }}
                >
                Total Base Price:
              </td>
            </tr>
            <tr>
              <td
                style={{
                  borderTop: "1px solid #ccc",
                  height: "40px",
                  fontWeight: "bold",
                }}
              >
                Packaging:
              </td>
            </tr>
            <tr>
              <td
                style={{
                  borderTop: "1px solid #ccc",
                  height: "40px",
                  fontWeight: "bold",
                }}
              >
                Freight:
              </td>
            </tr>
            <tr>
              <td
                style={{
                  borderTop: "1px solid #ccc",
                  height: "40px",
                  fontWeight: "bold",
                }}
              >
                Credit Cost:
              </td>
            </tr>
            <tr>
              <td
                style={{
                  borderTop: "1px solid #ccc",
                  height: "40px",
                  fontWeight: "bold",
                }}
              >
                Total Amount:
              </td>
            </tr>
            <tr>
              <td
                style={{
                  borderTop: "1px solid #ccc",
                  height: "40px",
                  fontWeight: "bold",
                }}
              >
                GST Amount:
              </td>
            </tr>
            <tr>
              <td
                style={{
                  borderTop: "1px solid #ccc",
                  height: "40px",
                  fontWeight: "bold",
                }}
              >
                Grand Total including all taxes:
              </td>
            </tr>
            <tr>
              <td
                style={{
                  borderTop: "1px solid #ccc",
                  height: "40px",
                  fontWeight: "bold",
                }}
              >
                Commercial Rating
              </td>
            </tr>
            <tr>
              <td
                colSpan={50}
                style={{
                  borderTop: "1px solid #ccc",
                  paddingY: "8px",
                  height: "40px",
                  fontWeight: "bold",
                  textAlign: "start",
                }}
              >
                Commercial Terms
              </td>
            </tr>
            <tr>
              <td
                style={{
                  borderTop: "1px solid #ccc",
                  padding: "8px",
                  fontWeight: "bold",
                }}
              >
                Delivery period (Lead Time)
              </td>
            </tr>
            <tr>
              <td
                style={{
                  borderTop: "1px solid #ccc",
                  padding: "8px",
                  fontWeight: "bold",
                }}
              >
                Payment Terms
              </td>
            </tr>
            <tr>
              <td
                style={{
                  borderTop: "1px solid #ccc",
                  padding: "8px",
                  fontWeight: "bold",
                }}
              >
                Freight
              </td>
            </tr>
            <tr>
              <td
                style={{
                  borderTop: "1px solid #ccc",
                  padding: "8px",
                  fontWeight: "bold",
                }}
              >
                Price Basis (Incoterms)
              </td>
            </tr>
            <tr>
              <td
                style={{
                  borderTop: "1px solid #ccc",
                  padding: "8px",
                  fontWeight: "bold",
                }}
              >
                Warranty Period
              </td>
            </tr>
            <tr>
              <td
                style={{
                  borderTop: "1px solid #ccc",
                  padding: "8px",
                  fontWeight: "bold",
                }}
              >
                Late Delivery Clause
              </td>
            </tr>
            <tr>
              <td
                style={{
                  borderTop: "1px solid #ccc",
                  padding: "8px",
                  fontWeight: "bold",
                }}
              >
                Attachment 1
              </td>
            </tr>
            <tr>
              <td
                style={{
                  borderTop: "1px solid #ccc",
                  padding: "8px",
                  fontWeight: "bold",
                }}
              >
                Attachment 2
              </td>
            </tr>
            <tr>
              <td
                style={{
                  borderTop: "1px solid #ccc",
                  padding: "8px",
                  fontWeight: "bold",
                }}
              >
                Attachment 3
              </td>
            </tr>
          </tbody>
        </table>

        {/* Scrollable Vendor Table */}
        <Box sx={{ overflowX: "scroll" }}>
          <table
            id="rightTable"
            style={{
              borderCollapse: "collapse",
              minWidth: `${bidData && bidData?.length * 250}px`,
              textAlign: "center",
              border: "1px solid #ccc",
              borderLeft: "none",
            }}
          >
            <thead>
              <tr>
                <th
                  style={{
                    padding: "9px",
                    borderRight: "1px solid #ccc",
                  }}
                  rowSpan={4}
                >
                  Item Descp.
                </th>
                <th
                  style={{
                    padding: "9px",
                    width: "70px",
                    borderRight: "1px solid #ccc",
                  }}
                  rowSpan={4}
                >
                  UOM
                </th>
                <th
                  style={{
                    padding: "9px",
                    width: "70px",
                    borderRight: "1px solid #ccc",
                  }}
                  rowSpan={4}
                >
                  Quantity
                </th>
                <th
                  style={{
                    padding: "9px",
                    width: "100px",
                    borderRight: "1px solid #ccc",
                  }}
                  rowSpan={4}
                >
                  GST <br />
                  (in %)
                </th>
                <th
                  style={{
                    padding: "9px",
                    width: "70px",
                    borderRight: "1px solid #ccc",
                  }}
                  rowSpan={4}
                >
                  Previous PO Rate (in &#8377;)
                </th>
                <th
                  style={{
                    padding: "9px",
                    width: "70px",
                    borderRight: "1px solid #ccc",
                  }}
                  rowSpan={4}
                >
                  SAP PR
                </th>

                {/* Vendor Name Row */}
                {Object.entries(groupedData).map(([vendor, bids], i) => (
                  <th
                    key={`vendor-name-${vendor}-${i}`}
                    colSpan={getVendorColSpan(vendor, bids)}
                    style={{
                      padding: "9px",
                      borderBottom: "1px solid #ccc",
                      borderRight: "1px solid #ccc",
                      textAlign: "center",
                    }}
                  >
                    {vendor.split("-")[1]}
                  </th>
                ))}
              </tr>

              <tr>
                {/* Vendor Code Row */}
                {Object.entries(groupedData).map(([vendor, bids], i) => (
                  <th
                    key={`vendor-code-${vendor}`}
                    colSpan={getVendorColSpan(vendor, bids)}
                    style={{
                      padding: "9px",
                      borderBottom: "1px solid #ccc",
                      borderRight: "1px solid #ccc",
                      textAlign: "center",
                    }}
                  >
                    {vendor.split("-")[0]}
                  </th>
                ))}
              </tr>

              <tr>
                {/* Bid Type Row */}
                {Object.entries(groupedData).flatMap(([vendor, bids]) =>
                  bids.map((_, i) => (
                    <th
                      key={`${vendor}-quote-${i}`}
                      colSpan={
                        (columnVisibility[`${vendor}-${i}`]?.showRate ? 1 : 0) +
                        2 +
                        (columnVisibility[`${vendor}-${i}`]?.showRemark ? 1 : 0)
                      }
                      style={{
                        padding: "8px",
                        borderBottom: "1px solid #ccc",
                        borderRight: "1px solid #ccc",
                        textAlign: "center",
                      }}
                    >
                      {i === 0 ? "Initial Quote" : "Final Quote"}
                    </th>
                  ))
                )}
              </tr>

              <tr>
                {/* Sub-columns for each bid */}
                {Object.entries(groupedData).flatMap(([vendor, bids]) =>
                  bids.map((_, i) => {
                    const { showRate, showRemark } =
                      columnVisibility[`${vendor}-${i}`];
                    const columns = [];

                    if (showRate) {
                      columns.push(
                        <th
                          key={`${vendor}-rate-${i}`}
                          style={{
                            width: "100px",
                            borderTop: "1px solid #ccc",
                            borderRight: "1px solid #ccc",
                          }}
                        >
                          Rate
                        </th>
                      );
                    }

                    columns.push(
                      <th
                        key={`${vendor}-inr-rate-${i}`}
                        style={{
                          minWidth: "60px",
                          borderTop: "1px solid #ccc",
                          borderRight: "1px solid #ccc",
                        }}
                      >
                        Rate (in &#8377;)
                      </th>,
                      <th
                        key={`${vendor}-total-${i}`}
                        style={{
                          width: "100px",
                          borderTop: "1px solid #ccc",
                          borderRight: "1px solid #ccc",
                        }}
                      >
                        Total (in &#8377;)
                      </th>
                    );

                    if (showRemark) {
                      columns.push(
                        <th
                          key={`${vendor}-remark-${i}`}
                          style={{
                            width: "100px",
                            borderTop: "1px solid #ccc",
                            borderRight: "1px solid #ccc",
                          }}
                        >
                          Remark
                        </th>
                      );
                    }

                    return columns;
                  })
                )}
              </tr>
            </thead>

            <tbody>
              {Object.entries(groupedWorkItems).map(
                ([section, items], sectionIndex) => (
                  <React.Fragment key={sectionIndex}>
                    {/* Section header row */}
                    <tr
                      style={{
                        borderTop: "1px solid #ccc",
                        fontWeight: "bold",
                      }}
                    >
                      <td
                        colSpan={6}
                        style={{
                          borderTop: "1px solid #ccc",
                          borderRight: "1px solid #ccc",
                        }}
                      ></td>

                      {Object.entries(groupedData).flatMap(([vendor, bids]) =>
                        bids.map((bid, i) => {
                          const sectionItems = bid.bidWorkItems.filter((bi) =>
                            items.find((wi) => wi.code === bi.code)
                          );
                          const sectionTotal = sectionItems.reduce(
                            (sum, si) => {
                              const rate = si.rate || 0;
                              const quantity = si.quantity || 0;
                              const conversionRate = si.currency?.rate || 1;
                              const gst = si.gst || 0;
                              const base = rate * quantity * conversionRate;
                              return sum + base;
                            },
                            0
                          );

                          return (
                            <td
                              key={`${vendor}-section-total-${i}`}
                              colSpan={
                                (columnVisibility[`${vendor}-${i}`]?.showRate
                                  ? 1
                                  : 0) +
                                2 +
                                (columnVisibility[`${vendor}-${i}`]?.showRemark
                                  ? 1
                                  : 0)
                              }
                              style={{
                                fontWeight: "bold",
                                textAlign: "right",
                                padding: "10px",
                                borderTop: "1px solid #ccc",
                                borderRight: "1px solid #ccc",
                              }}
                            >
                              &#8377;{" "}
                              {sectionTotal.toLocaleString("en-IN", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </td>
                          );
                        })
                      )}
                    </tr>

                    {/* Item rows */}
                    {items
                      .sort((a, b) => a.itemOrder - b.itemOrder)
                      .map((item, rowIndex) => (
                        <tr key={rowIndex}>
                          {/* Item fixed columns */}
                          <td
                            style={{
                              borderTop: "1px solid #ccc",
                              borderRight: "1px solid #ccc",
                              padding: "8px",
                            }}
                          >
                            {item?.description && (
                              <Tooltip
                                title={item.description}
                                arrow
                                placement="top"
                              >
                                <IconButton size="small" sx={{ padding: 0 }}>
                                  <InfoOutlinedIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </td>
                          <td
                            style={{
                              borderTop: "1px solid #ccc",
                              borderRight: "1px solid #ccc",
                              padding: "8px",
                            }}
                          >
                            {item.unit}
                          </td>
                          <td
                            style={{
                              borderTop: "1px solid #ccc",
                              borderRight: "1px solid #ccc",
                              padding: "8px",
                            }}
                          >
                            {item.quantity}
                          </td>
                          <td
                            style={{
                              borderTop: "1px solid #ccc",
                              borderRight: "1px solid #ccc",
                              padding: "8px",
                            }}
                          >
                            {item.gst}
                          </td>
                          <td
                            style={{
                              borderTop: "1px solid #ccc",
                              borderRight: "1px solid #ccc",
                              padding: "8px",
                            }}
                          >
                            {(item?.poRate ?? 0).toLocaleString("en-IN", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </td>
                          <td
                            style={{
                              borderTop: "1px solid #ccc",
                              borderRight: "1px solid #ccc",
                              padding: "8px",
                            }}
                          >
                            {item.sapPr}
                          </td>

                          {/* Vendor bid columns */}
                          {Object.entries(groupedData).flatMap(
                            ([vendor, bids]) =>
                              bids.map((bid, i) => {
                                const normalize = (str) =>
                                  str
                                    ?.replace(/\s+/g, " ")
                                    .trim()
                                    .toLowerCase();
                                const matchedItem = bid?.bidWorkItems.find(
                                  (bi) =>
                                    bi.code === item.code &&
                                    normalize(bi.description) ===
                                      normalize(item.description) &&
                                    bi.quantity === item.quantity &&
                                    bi.unit === item.unit
                                    // bi.gst === item.gst
                                );

                                const rate = matchedItem?.rate || 0;
                                const conversionRate =
                                  matchedItem?.currency?.rate || 1;
                                const quantity = matchedItem?.quantity || 0;
                                const gst = matchedItem?.gst || 0;
                                const baseTotal =
                                  rate * conversionRate * quantity;
                                const matchedItemTotal =
                                  baseTotal;

                                const { showRate, showRemark } =
                                  columnVisibility[`${vendor}-${i}`];

                                return (
                                  <React.Fragment
                                    key={`${vendor}-item-${i}-${rowIndex}`}
                                  >
                                    {showRate && (
                                      <td
                                        style={{
                                          borderTop: "1px solid #ccc",
                                          borderRight: "1px solid #ccc",
                                          padding: "8px",
                                          textAlign: "right",
                                          whiteSpace: "nowrap",
                                        }}
                                      >
                                        {
                                          matchedItem?.currency?.currency
                                            ?.symbol
                                        }{" "}
                                        {matchedItem?.rate.toLocaleString(
                                          "en-IN",
                                          {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                          }
                                        ) ?? ""}
                                      </td>
                                    )}
                                    <td
                                      style={{
                                        borderTop: "1px solid #ccc",
                                        borderRight: "1px solid #ccc",
                                        padding: "8px",
                                        textAlign: "right",
                                      }}
                                    >
                                      {(rate * conversionRate).toLocaleString(
                                        "en-IN",
                                        {
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 2,
                                        }
                                      )}
                                    </td>
                                    <td
                                      style={{
                                        borderTop: "1px solid #ccc",
                                        borderRight: "1px solid #ccc",
                                        padding: "8px",
                                        textAlign: "right",
                                      }}
                                    >
                                      {matchedItem?.currency?.currency
                                        ?.shortform !== CURRENCY_INR
                                        ? matchedItemTotal.toLocaleString(
                                            "en-IN",
                                            {
                                              minimumFractionDigits: 2,
                                              maximumFractionDigits: 2,
                                            }
                                          )
                                        : (
                                            matchedItem?.total || 0
                                          ).toLocaleString("en-IN", {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                          })}
                                    </td>
                                    {showRemark && (
                                      <td
                                        style={{
                                          borderTop: "1px solid #ccc",
                                          borderRight: "1px solid #ccc",
                                          padding: "8px",
                                          textAlign: "center",
                                        }}
                                      >
                                        {matchedItem?.remark ? (
                                          <Tooltip
                                            title={matchedItem.remark}
                                            arrow
                                            placement="top"
                                          >
                                            <IconButton
                                              size="small"
                                              sx={{ padding: 0 }}
                                            >
                                              <InfoOutlinedIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                        ) : (
                                          "-"
                                        )}
                                      </td>
                                    )}
                                  </React.Fragment>
                                );
                              })
                          )}
                        </tr>
                      ))}
                  </React.Fragment>
                )
              )}

              <tr style={{ fontWeight: "bold", borderTop: "1px solid #ccc" }}>
                <td colSpan={6} style={{ borderRight: "1px solid #ccc" }}></td>
                {Object.entries(groupedData).flatMap(([vendor, bids]) =>
                  bids.map((bid, i) => {
                    const baseTotal = bid.bidWorkItems.reduce((acc, item) => {
                      const rate = item.rate || 0;
                      const quantity = item.quantity || 0;
                      const conversionRate = item.currency?.rate || 1;

                      const baseTotal = rate * quantity;

                      if (item.currency.currency.shortform !== CURRENCY_INR) {
                        return acc + baseTotal * conversionRate;
                      } else {
                        return acc + baseTotal;
                      }
                    }, 0);

                    return (
                      <td
                        key={bid.bidId}
                        colSpan={
                          (columnVisibility[`${vendor}-${i}`]?.showRate
                            ? 1
                            : 0) +
                          2 +
                          (columnVisibility[`${vendor}-${i}`]?.showRemark
                            ? 1
                            : 0)
                        }
                        style={{
                          textAlign: "end",
                          paddingInline: "10px",
                          height: "40px",
                          borderRight: "1px solid #ccc",
                        }}
                      >
                        &#8377;{" "}
                        {new Intl.NumberFormat("en-IN", {
                          style: "decimal",
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        }).format(baseTotal)}
                      </td>
                    );
                  })
                )}
              </tr>
              <tr style={{ fontWeight: "bold", borderTop: "1px solid #ccc" }}>
                <td colSpan={6} style={{ borderRight: "1px solid #ccc" }}></td>
                {Object.entries(groupedData).flatMap(([vendor, bids]) =>
                  bids.map((bid, i) => {
                    return (
                      <td
                        key={bid.bidId}
                        colSpan={
                          (columnVisibility[`${vendor}-${i}`]?.showRate
                            ? 1
                            : 0) +
                          2 +
                          (columnVisibility[`${vendor}-${i}`]?.showRemark
                            ? 1
                            : 0)
                        }
                        style={{
                          borderRight: "1px solid #ccc",
                          paddingInline: "10px",
                          textAlign: "end",
                        }}
                      >
                        {storedUser.userRole[0] === "BUYER" && (rfq?.status !== "AWARDED" && rfq?.status !== "REJECTED") &&
                        !bid?.valueUpdated ? (
                          <>
                            <input
                              type="number"
                              style={{
                                width: "50%",
                                margin: 0,
                                padding: "10px",
                                textAlign: "end",
                              }}
                              defaultValue={
                                data.packagingValue || bid?.packagingValue || 0
                              }
                              onChange={(e) =>
                                handleChange(
                                  bid.bidId,
                                  "packagingValue",
                                  e.target.value
                                )
                              }
                            />
                            <select
                              style={{
                                width: "30%",
                                margin: 0,
                                padding: "10px",
                              }}
                              defaultValue={data.packagingType || bid?.packagingType}
                              onChange={(e) =>
                                handleChange(
                                  bid.bidId,
                                  "packagingType",
                                  e.target.value
                                )
                              }
                            >
                              <option value="">--Choose--</option>
                              <option value="value">&#8377;</option>
                              <option value="percentage">%</option>
                            </select>
                          </>
                        ) : (
                          `${bid?.packagingType === "value" ? `₹` : ""} ${
                            bid?.packagingValue
                          } ${bid?.packagingType === "percentage" ? `%` : ""}`
                        )}
                      </td>
                    );
                  })
                )}
              </tr>
              <tr style={{ fontWeight: "bold", borderTop: "1px solid #ccc" }}>
                <td colSpan={6} style={{ borderRight: "1px solid #ccc" }}></td>
                {Object.entries(groupedData).flatMap(([vendor, bids]) =>
                  bids.map((bid, i) => {
                    return (
                      <td
                        key={bid.bidId}
                        colSpan={
                          (columnVisibility[`${vendor}-${i}`]?.showRate
                            ? 1
                            : 0) +
                          2 +
                          (columnVisibility[`${vendor}-${i}`]?.showRemark
                            ? 1
                            : 0)
                        }
                        style={{
                          borderRight: "1px solid #ccc",
                          paddingInline: "10px",
                          textAlign: "end",
                        }}
                      >
                        {storedUser.userRole[0] === "BUYER" && (rfq?.status !== "AWARDED" && rfq?.status !== "REJECTED") &&
                        !bid?.valueUpdated ? (
                          <>
                            <input
                              type="number"
                              style={{
                                width: "50%",
                                margin: 0,
                                padding: "10px",
                                textAlign: "end",
                              }}
                              defaultValue={
                                data.freightValue || bid?.freightValue || 0
                              }
                              onChange={(e) =>
                                handleChange(
                                  bid.bidId,
                                  "freightValue",
                                  e.target.value
                                )
                              }
                            />
                            <select
                              style={{
                                width: "30%",
                                margin: 0,
                                padding: "10px",
                              }}
                              defaultValue={data.freightType || bid?.freightType}
                              onChange={(e) =>
                                handleChange(
                                  bid.bidId,
                                  "freightType",
                                  e.target.value
                                )
                              }
                            >
                              <option value="">--Choose--</option>
                              <option value="value">&#8377;</option>
                              <option value="percentage">%</option>
                            </select>
                          </>
                        ) : (
                          `${bid?.freightType === "value" ? `₹` : ""} ${
                            bid?.freightValue
                          } ${bid?.freightType === "percentage" ? `%` : ""}`
                        )}
                      </td>
                    );
                  })
                )}
              </tr>
              <tr style={{ fontWeight: "bold", borderTop: "1px solid #ccc" }}>
                <td colSpan={6} style={{ borderRight: "1px solid #ccc" }}></td>
                {Object.entries(groupedData).flatMap(([vendor, bids]) =>
                  bids.map((bid, i) => {
                    return (
                      <td
                        key={bid.bidId}
                        colSpan={
                          (columnVisibility[`${vendor}-${i}`]?.showRate
                            ? 1
                            : 0) +
                          2 +
                          (columnVisibility[`${vendor}-${i}`]?.showRemark
                            ? 1
                            : 0)
                        }
                        style={{
                          borderRight: "1px solid #ccc",
                          paddingInline: "10px",
                          textAlign: "end",
                        }}
                      >
                        {storedUser.userRole[0] === "BUYER" && (rfq?.status !== "AWARDED" && rfq?.status !== "REJECTED") &&
                        !bid?.valueUpdated ? (
                          <>
                            <input
                              type="number"
                              style={{
                                width: "50%",
                                margin: 0,
                                padding: "10px",
                                textAlign: "end",
                              }}
                              defaultValue={
                                data.creditValue || bid?.creditValue || 0
                              }
                              onChange={(e) =>
                                handleChange(
                                  bid.bidId,
                                  "creditValue",
                                  e.target.value
                                )
                              }
                            />
                            <select
                              style={{
                                width: "30%",
                                margin: 0,
                                padding: "10px",
                              }}
                              defaultValue={data.creditType || bid?.creditType}
                              onChange={(e) =>
                                handleChange(
                                  bid.bidId,
                                  "creditType",
                                  e.target.value
                                )
                              }
                            >
                              <option value="">--Choose--</option>
                              <option value="value">&#8377;</option>
                              <option value="percentage">%</option>
                            </select>
                          </>
                        ) : (
                          `${bid?.creditType === "value" ? `₹` : ""} ${
                            bid?.creditValue
                          } ${bid?.creditType === "percentage" ? `%` : ""}`
                        )}
                      </td>
                    );
                  })
                )}
              </tr>

              <tr style={{ fontWeight: "bold", borderTop: "1px solid #ccc" }}>
                <td colSpan={6} style={{ borderRight: "1px solid #ccc" }}></td>
                {Object.entries(groupedData).flatMap(([vendor, bids]) =>
                  bids.map((bid, i) => {
                    const baseTotal = bid.bidWorkItems.reduce((acc, item) => {
                      const rate = item.rate || 0;
                      const quantity = item.quantity || 0;
                      const conversionRate = item.currency?.rate || 1;

                      const baseTotal = rate * quantity;

                      if (item.currency.currency.shortform !== CURRENCY_INR) {
                        return acc + baseTotal * conversionRate;
                      } else {
                        return acc + baseTotal;
                      }
                    }, 0);

                    const calculateExtra = (value, type) => {
                      if (!value || !type) return 0;
                      if (type === "percentage") {
                        return baseTotal * (value / 100);
                      }
                      return value;
                    };

                    const packagingExtra = calculateExtra(
                      bid.packagingValue,
                      bid.packagingType
                    );
                    const freightExtra = calculateExtra(
                      bid.freightValue,
                      bid.freightType
                    );
                    const creditExtra = calculateExtra(
                      bid.creditValue,
                      bid.creditType
                    );

                    const finalTotal =
                      baseTotal + packagingExtra + freightExtra + creditExtra;
                    return (
                      <td
                        key={bid.bidId}
                        colSpan={
                          (columnVisibility[`${vendor}-${i}`]?.showRate
                            ? 1
                            : 0) +
                          2 +
                          (columnVisibility[`${vendor}-${i}`]?.showRemark
                            ? 1
                            : 0)
                        }
                        style={{
                          textAlign: "end",
                          paddingInline: "10px",
                          height: "40px",
                          borderRight: "1px solid #ccc",
                        }}
                      >
                        &#8377;{" "}
                        {new Intl.NumberFormat("en-IN", {
                          style: "decimal",
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        }).format(finalTotal)}
                      </td>
                    );
                  })
                )}
              </tr>

              <tr style={{ fontWeight: "bold", borderTop: "1px solid #ccc" }}>
                <td colSpan={6} style={{ borderRight: "1px solid #ccc" }}></td>
                {Object.entries(groupedData).flatMap(([vendor, bids]) =>
                  bids.map((bid, i) => {
                    const gstTotal = bid.bidWorkItems.reduce((acc, item) => {
                      const rate = item.rate || 0;
                      const quantity = item.quantity || 0;
                      const conversionRate = item.currency?.rate || 1;
                      const gst = item.gst || 0;

                      const baseTotal = rate * quantity;
                      const gstAmount = baseTotal * (gst / 100);

                      if (item.currency.currency.shortform !== CURRENCY_INR) {
                        return acc + gstAmount * conversionRate;
                      } else {
                        return acc + gstAmount;
                      }
                    }, 0);
                    return (
                      <td
                        key={bid.bidId}
                        colSpan={
                          (columnVisibility[`${vendor}-${i}`]?.showRate
                            ? 1
                            : 0) +
                          2 +
                          (columnVisibility[`${vendor}-${i}`]?.showRemark
                            ? 1
                            : 0)
                        }
                        style={{
                          textAlign: "end",
                          paddingInline: "10px",
                          height: "40px",
                          borderRight: "1px solid #ccc",
                        }}
                      >
                        &#8377;{" "}
                        {new Intl.NumberFormat("en-IN", {
                          style: "decimal",
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        }).format(gstTotal)}
                      </td>
                    );
                  })
                )}
              </tr>

              <tr style={{ fontWeight: "bold", borderTop: "1px solid #ccc" }}>
                <td colSpan={6} style={{ borderRight: "1px solid #ccc" }}></td>
                {Object.entries(groupedData).flatMap(([vendor, bids]) =>
                  bids.map((bid, i) => {
                    const grandTotal = bid.bidWorkItems.reduce((acc, item) => {
                      const rate = item.rate || 0;
                      const quantity = item.quantity || 0;
                      const conversionRate = item.currency?.rate || 1;
                      const gst = item.gst || 0;

                      const baseTotal = rate * quantity;
                      const gstAmount = baseTotal * (gst / 100);

                      if (item.currency.currency.shortform !== CURRENCY_INR) {
                        return acc + (baseTotal + gstAmount) * conversionRate;
                      } else {
                        return acc + baseTotal + gstAmount;
                      }
                    }, 0);

                    const baseTotal = bid.bidWorkItems.reduce((acc, item) => {
                      const rate = item.rate || 0;
                      const quantity = item.quantity || 0;
                      const conversionRate = item.currency?.rate || 1;

                      const baseTotal = rate * quantity;

                      if (item.currency.currency.shortform !== CURRENCY_INR) {
                        return acc + baseTotal * conversionRate;
                      } else {
                        return acc + baseTotal;
                      }
                    }, 0);

                    const calculateExtra = (value, type) => {
                      if (!value || !type) return 0;
                      if (type === "percentage") {
                        return baseTotal * (value / 100);
                      }
                      return value;
                    };
                    
                      const packagingExtra = calculateExtra(
                      bid.packagingValue,
                      bid.packagingType
                    );
                    const freightExtra = calculateExtra(
                      bid.freightValue,
                      bid.freightType
                    );
                    const creditExtra = calculateExtra(
                      bid.creditValue,
                      bid.creditType
                    );

                    const finalTotal = grandTotal + packagingExtra + freightExtra + creditExtra;
                    return (
                      <td
                        key={bid.bidId}
                        colSpan={
                          (columnVisibility[`${vendor}-${i}`]?.showRate
                            ? 1
                            : 0) +
                          2 +
                          (columnVisibility[`${vendor}-${i}`]?.showRemark
                            ? 1
                            : 0)
                        }
                        style={{
                          textAlign: "end",
                          paddingInline: "10px",
                          height: "40px",
                          borderRight: "1px solid #ccc",
                        }}
                      >
                        &#8377;{" "}
                        {new Intl.NumberFormat("en-IN", {
                          style: "decimal",
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        }).format(finalTotal)}
                      </td>
                    );
                  })
                )}
              </tr>              

              <tr style={{ fontWeight: "bold", borderTop: "1px solid #ccc" }}>
                <td colSpan={6} style={{ borderRight: "1px solid #ccc" }}></td>
                {Object.entries(groupedData).flatMap(([vendor, bids]) =>
                  bids.map((bid, i) => {
                    const isLatest = i === bids.length - 1; // Only highlight the latest bid
                    const baseTotal = bid.bidWorkItems.reduce((acc, item) => {
                      const rate = item.rate || 0;
                      const quantity = item.quantity || 0;
                      const conversionRate = item.currency?.rate || 1;

                      const subTotal = rate * quantity;
                      return (
                        acc +
                        (item.currency.currency.shortform !== CURRENCY_INR
                          ? subTotal * conversionRate
                          : subTotal)
                      );
                    }, 0);

                    const calculateExtra = (value, type) => {
                      if (!value || !type) return 0;
                      if (type === "percentage") {
                        return baseTotal * (value / 100);
                      }
                      return value;
                    };

                    const packagingExtra = calculateExtra(
                      bid.packagingValue,
                      bid.packagingType
                    );
                    const freightExtra = calculateExtra(
                      bid.freightValue,
                      bid.freightType
                    );
                    const creditExtra = calculateExtra(
                      bid.creditValue,
                      bid.creditType
                    );

                    const finalTotal =
                      baseTotal + packagingExtra + freightExtra + creditExtra;

                    let bgColor = "white";
                    if (isLatest && finalTotal === lowest?.total) bgColor = "lightgreen";
                    else if (isLatest && finalTotal === secondLowest?.total) bgColor = "yellow";

                    const tag =
                      isLatest && finalTotal === lowest?.total
                        ? "L1"
                        : isLatest && finalTotal === secondLowest?.total
                        ? "L2"
                        : "";

                    return (
                      <td
                        key={`${vendor}-rating-${i}`}
                        colSpan={
                          (columnVisibility[`${vendor}-${i}`]?.showRate
                            ? 1
                            : 0) +
                          2 +
                          (columnVisibility[`${vendor}-${i}`]?.showRemark
                            ? 1
                            : 0)
                        }
                        style={{
                          height: "40px",
                          border: "1px solid #ccc",
                          fontWeight: "bold",
                          backgroundColor: bgColor,
                        }}
                      >
                        {tag}
                      </td>
                    );
                  })
                )}
              </tr>

              <tr style={{ fontWeight: "bold", borderTop: "1px solid #ccc" }}>
                <td colSpan={500}></td>
              </tr>
              <tr style={{ fontWeight: "bold", borderTop: "1px solid #ccc" }}>
                <td colSpan={6} style={{ borderRight: "1px solid #ccc" }}></td>
                {Object.entries(groupedData).flatMap(([vendor, bids]) =>
                  bids.map((bid, i) => (
                    <td
                      key={bid.bidId}
                      colSpan={
                        (columnVisibility[`${vendor}-${i}`]?.showRate ? 1 : 0) +
                        2 +
                        (columnVisibility[`${vendor}-${i}`]?.showRemark ? 1 : 0)
                      }
                      style={{
                        padding: "10.5px",
                        borderRight: "1px solid #ccc",
                      }}
                    >
                      {bid.deliveryTerms || "N/A"}
                    </td>
                  ))
                )}
              </tr>
              <tr style={{ fontWeight: "bold", borderTop: "1px solid #ccc" }}>
                <td colSpan={6} style={{ borderRight: "1px solid #ccc" }}></td>
                {Object.entries(groupedData).flatMap(([vendor, bids]) =>
                  bids.map((bid, i) => (
                    <td
                      key={bid.bidId}
                      colSpan={
                        (columnVisibility[`${vendor}-${i}`]?.showRate ? 1 : 0) +
                        2 +
                        (columnVisibility[`${vendor}-${i}`]?.showRemark ? 1 : 0)
                      }
                      style={{
                        padding: "10.5px",
                        borderRight: "1px solid #ccc",
                      }}
                    >
                      {bid.paymentTerms || "N/A"}
                    </td>
                  ))
                )}
              </tr>
              <tr style={{ fontWeight: "bold", borderTop: "1px solid #ccc" }}>
                <td colSpan={6} style={{ borderRight: "1px solid #ccc" }}></td>
                {Object.entries(groupedData).flatMap(([vendor, bids]) =>
                  bids.map((bid, i) => (
                    <td
                      key={bid.bidId}
                      colSpan={
                        (columnVisibility[`${vendor}-${i}`]?.showRate ? 1 : 0) +
                        2 +
                        (columnVisibility[`${vendor}-${i}`]?.showRemark ? 1 : 0)
                      }
                      style={{
                        padding: "10.5px",
                        borderRight: "1px solid #ccc",
                      }}
                    >
                      {bid.freight || "N/A"}
                    </td>
                  ))
                )}
              </tr>
              <tr style={{ fontWeight: "bold", borderTop: "1px solid #ccc" }}>
                <td colSpan={6} style={{ borderRight: "1px solid #ccc" }}></td>
                {Object.entries(groupedData).flatMap(([vendor, bids]) =>
                  bids.map((bid, i) => (
                    <td
                      key={bid.bidId}
                      colSpan={
                        (columnVisibility[`${vendor}-${i}`]?.showRate ? 1 : 0) +
                        2 +
                        (columnVisibility[`${vendor}-${i}`]?.showRemark ? 1 : 0)
                      }
                      style={{
                        padding: "10.5px",
                        borderRight: "1px solid #ccc",
                      }}
                    >
                      {bid.priceBasis || "N/A"}
                    </td>
                  ))
                )}
              </tr>
              <tr style={{ fontWeight: "bold", borderTop: "1px solid #ccc" }}>
                <td colSpan={6} style={{ borderRight: "1px solid #ccc" }}></td>
                {Object.entries(groupedData).flatMap(([vendor, bids]) =>
                  bids.map((bid, i) => (
                    <td
                      key={bid.bidId}
                      colSpan={
                        (columnVisibility[`${vendor}-${i}`]?.showRate ? 1 : 0) +
                        2 +
                        (columnVisibility[`${vendor}-${i}`]?.showRemark ? 1 : 0)
                      }
                      style={{
                        padding: "10.5px",
                        borderRight: "1px solid #ccc",
                      }}
                    >
                      {bid.warrantyPeriod || "N/A"}
                    </td>
                  ))
                )}
              </tr>
              <tr style={{ fontWeight: "bold", borderTop: "1px solid #ccc" }}>
                <td colSpan={6} style={{ borderRight: "1px solid #ccc" }}></td>
                {Object.entries(groupedData).flatMap(([vendor, bids]) =>
                  bids.map((bid, i) => (
                    <td
                      key={bid.bidId}
                      colSpan={
                        (columnVisibility[`${vendor}-${i}`]?.showRate ? 1 : 0) +
                        2 +
                        (columnVisibility[`${vendor}-${i}`]?.showRemark ? 1 : 0)
                      }
                      style={{
                        padding: "10.5px",
                        borderRight: "1px solid #ccc",
                      }}
                    >
                      {bid.deliveryClause || "N/A"}
                    </td>
                  ))
                )}
              </tr>
              <tr style={{ fontWeight: "bold", borderTop: "1px solid #ccc" }}>
                <td colSpan={6} style={{ borderRight: "1px solid #ccc" }}></td>
                {Object.entries(groupedData).flatMap(([vendor, bids]) =>
                  bids.map((bid, i) => (
                    <td
                      key={bid.bidId}
                      colSpan={
                        (columnVisibility[`${vendor}-${i}`]?.showRate ? 1 : 0) +
                        2 +
                        (columnVisibility[`${vendor}-${i}`]?.showRemark ? 1 : 0)
                      }
                      style={{
                        padding: "9.5px",
                        borderRight: "1px solid #ccc",
                      }}
                    >
                      {(bid.attachment1 && bid.attachment1 !== "N/A" && (
                        <Button
                          sx={{
                            margin: "0 !important",
                            padding: "0 !important",
                            width: "max-content !important",
                            fontSize: "12px !important",
                            textDecoration: "underline",
                            textAlign: "start",
                          }}
                          onClick={() => viewPDF(bid.attachment1, storedUser)}
                        >
                          View
                        </Button>
                      )) ||
                        "N/A"}
                    </td>
                  ))
                )}
              </tr>
              <tr style={{ fontWeight: "bold", borderTop: "1px solid #ccc" }}>
                <td colSpan={6} style={{ borderRight: "1px solid #ccc" }}></td>
                {Object.entries(groupedData).flatMap(([vendor, bids]) =>
                  bids.map((bid, i) => (
                    <td
                      key={bid.bidId}
                      colSpan={
                        (columnVisibility[`${vendor}-${i}`]?.showRate ? 1 : 0) +
                        2 +
                        (columnVisibility[`${vendor}-${i}`]?.showRemark ? 1 : 0)
                      }
                      style={{
                        padding: "9.5px",
                        borderRight: "1px solid #ccc",
                      }}
                    >
                      {(bid.attachment2 && bid.attachment2 !== "N/A" && (
                        <Button
                          sx={{
                            margin: "0 !important",
                            padding: "0 !important",
                            width: "max-content !important",
                            fontSize: "12px !important",
                            textDecoration: "underline",
                            textAlign: "start",
                          }}
                          onClick={() => viewPDF(bid.attachment2, storedUser)}
                        >
                          View
                        </Button>
                      )) ||
                        "N/A"}
                    </td>
                  ))
                )}
              </tr>
              <tr style={{ fontWeight: "bold", borderTop: "1px solid #ccc" }}>
                <td colSpan={6} style={{ borderRight: "1px solid #ccc" }}></td>
                {Object.entries(groupedData).flatMap(([vendor, bids]) =>
                  bids.map((bid, i) => (
                    <td
                      key={bid.bidId}
                      colSpan={
                        (columnVisibility[`${vendor}-${i}`]?.showRate ? 1 : 0) +
                        2 +
                        (columnVisibility[`${vendor}-${i}`]?.showRemark ? 1 : 0)
                      }
                      style={{
                        padding: "9.5px",
                        borderRight: "1px solid #ccc",
                      }}
                    >
                      {(bid.attachment3 && bid.attachment3 !== "N/A" && (
                        <Button
                          sx={{
                            margin: "0 !important",
                            padding: "0 !important",
                            width: "max-content !important",
                            fontSize: "12px !important",
                            textDecoration: "underline",
                            textAlign: "start",
                          }}
                          onClick={() => viewPDF(bid.attachment3, storedUser)}
                        >
                          View
                        </Button>
                      )) ||
                        "N/A"}
                    </td>
                  ))
                )}
              </tr>
            </tbody>
          </table>
        </Box>
      </Box>
      {storedUser.userRole[0] === "BUYER" && rfq?.status !== "AWARDED" && rfq?.status !== "REJECTED" && (
        <Box sx={{ display: "flex", justifyContent: "end" }}>
          <Button
            className="btn-contained"
            onClick={enterValues}
            disabled={applyValue}
          >
            Apply Values
          </Button>
        </Box>
      )}
    </div>
  );
};

export default ViewAllBidsTableNew;